import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Phone } from 'lucide-react'
import { FixLogo } from '../FixLogo'
import { SUPPORT_PHONE_DISPLAY, SHOW_SUPPORT_PHONE } from '../../lib/contact'
import { TextUsLink } from '../TextUsLink'
import { useAppConfig } from '../../hooks/useAppConfig'

const POPULAR_COUNTIES = [
  { label: 'Greater London', slug: 'greater-london' },
  { label: 'Surrey', slug: 'surrey' },
  { label: 'Greater Manchester', slug: 'greater-manchester' },
  { label: 'West Midlands', slug: 'west-midlands' },
  { label: 'Kent', slug: 'kent' },
  { label: 'Essex', slug: 'essex' },
]

function DirectoryLinksColumn({
  title,
  basePath,
}: {
  title: string
  basePath: string
}) {
  // Static popular counties. This was a live getDirectoryCounties() call per
  // column (x4 modes) hitting a slow aggregation endpoint on every page load —
  // it sat on the critical path and pushed LCP out by several seconds.
  const links = POPULAR_COUNTIES

  return (
    <div>
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      <ul className="space-y-2 text-sm">
        {links.map(({ label, slug }) => (
          <li key={slug}>
            {/* Mode-qualified label so the four columns' links are unique (both
                visibly and for the "identical links, same purpose" audit). */}
            <Link to={`${basePath}/england/${slug}`} className="hover:text-white transition-colors">
              {title} <span aria-hidden="true">–</span> {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {
  const location = useLocation()
  const navigate = useNavigate()
  const isHome = location.pathname === '/'
  // Don't show the "Get help now" CTA to someone already inside a booking or
  // live job — it points back at the homepage funnel and creates a loop out of
  // a higher-intent flow.
  const inBookingFlow = /^\/(book|booking|job|purchase-complete)\b/.test(location.pathname)
  const { paywallEnabled } = useAppConfig()

  function handleGetHelp() {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      void navigate('/')
    }
  }

  return (
    <footer className="bg-gray-900 text-gray-400">
      {!inBookingFlow && <section className="border-b border-gray-800 py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Plumbing emergency right now?</h2>
          <p className="text-gray-400 mb-8 text-sm sm:text-base">
            {paywallEnabled
              ? "Don't waste time calling around. Let us handle it - small flat fee, fully refundable if we don't deliver."
              : "Don't waste time calling around. Let us handle it - completely free right now, we contact every plumber near you at once."}
          </p>
          <button
            type="button"
            onClick={handleGetHelp}
            className="w-full sm:w-auto inline-block bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-colors"
          >
            Get help now →
          </button>
          {SHOW_SUPPORT_PHONE && (
            <p className="mt-4 text-sm text-gray-500">
              Prefer to speak to someone?{' '}
              <TextUsLink className="text-white font-medium hover:underline">
                Text us · {SUPPORT_PHONE_DISPLAY}
              </TextUsLink>
            </p>
          )}
        </div>
      </section>}

      <div className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 text-white font-bold text-lg mb-1">
              <FixLogo size={20} />
              FixDelivered
            </div>
            <p className="text-sm text-gray-500 max-w-xs">Plumber matching - 24/7, UK wide.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/how-it-works" className="hover:text-white transition-colors">How it works</Link>
            <Link to="/help" className="hover:text-white transition-colors">Help &amp; advice</Link>
            {SHOW_SUPPORT_PHONE && (
              <TextUsLink className="hover:text-white transition-colors inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                Text us · {SUPPORT_PHONE_DISPLAY}
              </TextUsLink>
            )}
            <a href="mailto:support@fixdelivered.com" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 pb-10 border-b border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Company</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white transition-colors">How it works</Link></li>
              <li><Link to="/plumbers" className="hover:text-white transition-colors">Plumber Directory</Link></li>
              <li><Link to="/emergency-plumbers" className="hover:text-white transition-colors">Emergency Directory</Link></li>
              <li><Link to="/gas-safe-plumbers" className="hover:text-white transition-colors">Gas Safe Directory</Link></li>
              <li><Link to="/water-safe-plumbers" className="hover:text-white transition-colors">WaterSafe Directory</Link></li>
            </ul>
          </div>

          <DirectoryLinksColumn title="Emergency" basePath="/emergency-plumbers" />
          <DirectoryLinksColumn title="Gas Safe" basePath="/gas-safe-plumbers" />
          <DirectoryLinksColumn title="WaterSafe" basePath="/water-safe-plumbers" />
          <DirectoryLinksColumn title="Plumbers" basePath="/plumbers" />
        </div>

        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <span>© {new Date().getFullYear()} RLowe Limited. All rights reserved.</span>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-2 gap-y-1">
            <span>Registered in England &amp; Wales</span>
            <span className="text-gray-700" aria-hidden="true">
              ·
            </span>
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-700" aria-hidden="true">
              ·
            </span>
            <Link to="/terms" className="hover:text-gray-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
        </div>
      </div>
    </footer>
  )
}
